<template>
    <MainContent
        title="Listado de deportistas"
        :value="filters"
        :meta="bases?.meta || {}"
        @input="val => Object.assign(filters, val)"
        @updated="debouncedFetch"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:flex tw:w-full tw:items-center tw:justify-between tw:flex-col-reverse tw:md:flex-row tw:gap-4">
                            <!-- Buscador principal -->
                            <div class="tw:w-full">
                                <SearchComponent
                                    v-model="filters.search"
                                    data-cy="search_input"
                                />
                            </div>
                            <div class="tw:flex tw:flex-col-reverse tw:md:flex-row tw:justify-end tw:gap-4 tw:w-full">
                                <Button
                                    id="add-deportista"
                                    as="router-link"
                                    :to="{ name: 'back.bases.crear', params: { lista_id: listaId } }"
                                    icon="fas fa-plus"
                                    customClass="tw:text-xs tw:sm:text-sm tw:w-full tw:sm:w-fit tw:justify-center"
                                >
                                    Añadir nuevo deportista
                                </Button>

                                <OptionsDropdownComponent
                                    :options="accionesOptions"
                                    label="Opciones"
                                    data-cy="opciones-buscador"
                                    customClass="tw:text-xs tw:sm:text-sm tw:w-full tw:justify-center"
                                    containerClass="tw:w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="tw:flex tw:flex-col tw:gap-5">
                        <!-- Filtros secundarios -->
                        <BasesFilters
                            :filters="filters"
                            :categorias="categorias"
                            :equipos="equipos"
                            :disabled="loading"
                            @reset="resetFilters"
                            @update:filters="val => Object.assign(filters, val)"
                        />
                    </div>
                </div>
            </Card>
        </template>

        <BasesTable
            :bases="bases.data || []"
            :loading="loading"
            @refresh="fetch"
        />

        <!-- Skeleton footer (solo primera carga) -->
        <template v-if="!hasLoadedOnce && loading">
            <Card :custom-classes="'tw:flex tw:flex-col tw:items-center tw:justify-between tw:gap-2 tw:pl-4 tw:pr-4 tw:py-2 tw:mt-6'">
                <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                    <template #skeleton>
                        <div class="tw:flex tw:flex-col tw:w-full tw:md:flex-row tw:md:items-center tw:md:justify-between">
                            <div class="tw:flex tw:justify-between tw:items-center tw:gap-4">
                                <SkeletonItem height="tw:h-9" customClass="tw:w-24 tw:rounded-lg" />
                                <SkeletonItem height="tw:h-4" customClass="tw:w-36 tw:rounded tw:md:tw:hidden" />
                            </div>
                            <div class="tw:hidden tw:md:block">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-48 tw:rounded tw:mx-auto" />
                            </div>
                            <div class="tw:flex tw:justify-center tw:items-center tw:gap-1">
                                <SkeletonItem v-for="n in 3" :key="'pag-sk-' + n" height="tw:h-8" customClass="tw:w-8 tw:rounded" />
                            </div>
                        </div>
                    </template>
                </Skeleton>
            </Card>
        </template>
    </MainContent>
</template>

<script setup>
    import { ref, computed, watch } from 'vue'
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import BasesFilters from '@/modules/admin/bases/components/BasesFilters.vue';
    import BasesTable from '@/modules/admin/bases/components/BasesTable.vue';

    import { useBasesIndex } from '@/composables/admin/bases/useBasesIndex';

    const {
        bases,
        loading,
        filters,
        categorias,
        equipos,
        listaId,
        fetch,
        debouncedFetch,
        updateUrlQuery
    } = useBasesIndex();

    const hasLoadedOnce = ref(false);
    watch(bases, val => {
        if (val?.data?.length) {
            hasLoadedOnce.value = true;
        }
    });

    const accionesOptions = computed(() => [
        {
            to: { name: 'back.bases.informe', params: { lista_id: listaId.value } },
            label: 'VENTAS',
            icon: 'chart-bar'
        },
        {
            to: { name: 'back.bases.categorias.index', params: { lista_id: listaId.value } },
            label: 'Configurar precios categorías',
            icon: 'tags'
        },
        {
            to: { name: 'back.bases.equipos', params: { lista_id: listaId.value } },
            label: 'Equipos',
            icon: 'users'
        },
        {
            to: { name: 'back.bases.indumentarias.index', params: { lista_id: listaId.value } },
            label: 'Configurar indumentarias',
            icon: 'tshirt'
        },
        {
            to: { name: 'back.bases.renovar', params: { lista_id: listaId.value } },
            label: 'Renovar deportista',
            icon: 'sync'
        },
        {
            to: { name: 'back.bases.fields', params: { lista_id: listaId.value } },
            label: 'Campos personalizados',
            icon: 'cog'
        }
    ])

    function resetFilters() {
        Object.assign(filters.value, {
            search: '',
            search_for: '',
            categoria: '',
            metodo_pago: '',
            equipo: '',
            page: 1,
            per_page: 10
        })
        fetch()
        updateUrlQuery()
    }
</script>
